<template>
  <div class="settings-page">
    <header class="header">
      <el-button text class="back-btn" aria-label="返回" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <h1 class="title">系统设置</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-card class="setting-card" shadow="never">
        <template #header>
          <div class="card-header">
            <el-icon class="card-icon" color="var(--primary-color)">
              <ChromeFilled />
            </el-icon>
            <span>语言设置</span>
          </div>
        </template>
        <el-radio-group
          v-model="currentLanguage"
          class="option-group"
          @change="changeLanguage"
        >
          <el-radio
            v-for="lang in languages"
            :key="lang.value"
            :value="lang.value"
            class="option-radio"
            border
          >
            {{ lang.label }}
          </el-radio>
        </el-radio-group>
      </el-card>

      <el-card class="setting-card" shadow="never">
        <template #header>
          <div class="card-header">
            <el-icon class="card-icon" color="#9f7aea">
              <Brush />
            </el-icon>
            <span>主题切换</span>
          </div>
        </template>
        <el-radio-group
          v-model="currentTheme"
          class="option-group"
          @change="changeTheme"
        >
          <el-radio
            v-for="theme in themes"
            :key="theme.value"
            :value="theme.value"
            class="option-radio"
            border
          >
            {{ theme.label }}
          </el-radio>
        </el-radio-group>
      </el-card>

      <el-card class="setting-card" shadow="never" :body-style="{ padding: 0 }">
        <template #header>
          <div class="card-header">
            <el-icon class="card-icon" color="#f39c12">
              <Bell />
            </el-icon>
            <span>通知设置</span>
          </div>
        </template>
        <div class="switch-list">
          <div class="switch-row">
            <span class="switch-label">消息通知</span>
            <el-switch
              v-model="notificationSettings.message"
              @change="saveNotificationSettings"
            />
          </div>
          <div class="switch-row">
            <span class="switch-label">任务提醒</span>
            <el-switch
              v-model="notificationSettings.task"
              @change="saveNotificationSettings"
            />
          </div>
          <div class="switch-row">
            <span class="switch-label">系统更新</span>
            <el-switch
              v-model="notificationSettings.update"
              @change="saveNotificationSettings"
            />
          </div>
        </div>
      </el-card>

      <el-card class="setting-card" shadow="never" :body-style="{ padding: 0 }">
        <template #header>
          <div class="card-header">
            <el-icon class="card-icon" color="#38b2ac">
              <Box />
            </el-icon>
            <span>仓库操作设置</span>
          </div>
        </template>
        <div class="switch-list">
          <div class="switch-row">
            <span class="switch-label">扫码后自动添加托盘</span>
            <el-switch
              v-model="warehouseSettings.autoAddPallet"
              @change="saveWarehouseSettings"
            />
          </div>
        </div>
      </el-card>

      <el-card class="setting-card" shadow="never">
        <template #header>
          <div class="card-header">
            <el-icon class="card-icon" color="#23b1c0">
              <HomeFilled />
            </el-icon>
            <span>默认页面</span>
          </div>
        </template>
        <el-radio-group
          v-model="currentDefaultPage"
          class="option-group"
          @change="changeDefaultPage"
        >
          <el-radio
            v-for="page in defaultPages"
            :key="page.value"
            :value="page.value"
            class="option-radio"
            border
          >
            {{ page.label }}
          </el-radio>
        </el-radio-group>
      </el-card>
    </main>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  ChromeFilled,
  Brush,
  Bell,
  Box,
  HomeFilled
} from '@element-plus/icons-vue'

export default {
  name: 'SystemSettings',
  components: {
    ChromeFilled,
    Brush,
    Bell,
    Box,
    HomeFilled
  },
  setup() {
    const router = useRouter()

    const languages = [
      { label: '简体中文', value: 'zh-CN' },
      { label: 'English', value: 'en-US' }
    ]

    const themes = [
      { label: '亮色主题', value: 'light' },
      { label: '深色主题', value: 'dark' },
      { label: '跟随系统', value: 'auto' }
    ]

    const defaultPages = [
      { label: '首页', value: 'home' },
      { label: '工作台', value: 'dashboard' },
      { label: '入库管理', value: 'warein' },
      { label: '出库管理', value: 'wareout' }
    ]

    const currentLanguage = ref('zh-CN')
    const currentTheme = ref('light')
    const notificationSettings = ref({
      message: true,
      task: true,
      update: true
    })
    const warehouseSettings = ref({
      autoAddPallet: false
    })
    const currentDefaultPage = ref('home')

    const applyTheme = (theme) => {
      if (theme === 'auto') {
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches
        document.documentElement.setAttribute(
          'data-theme',
          prefersDark ? 'dark' : 'light'
        )
      } else {
        document.documentElement.setAttribute('data-theme', theme)
      }
    }

    const loadSettings = () => {
      try {
        const savedLanguage = localStorage.getItem('systemLanguage')
        if (savedLanguage) currentLanguage.value = savedLanguage
      } catch (e) {
        console.warn('无法读取语言设置', e)
      }

      try {
        const savedTheme = localStorage.getItem('systemTheme')
        if (savedTheme) currentTheme.value = savedTheme
      } catch (e) {
        console.warn('无法读取主题设置', e)
      }

      try {
        const savedNotifications = localStorage.getItem('notificationSettings')
        if (savedNotifications) {
          notificationSettings.value = JSON.parse(savedNotifications)
        }
      } catch (e) {
        console.warn('无法读取通知设置', e)
      }

      try {
        const savedDefaultPage = localStorage.getItem('defaultPage')
        if (savedDefaultPage) currentDefaultPage.value = savedDefaultPage
      } catch (e) {
        console.warn('无法读取默认页面设置', e)
      }

      try {
        const savedWarehouseSettings = localStorage.getItem('warehouseSettings')
        if (savedWarehouseSettings) {
          warehouseSettings.value = JSON.parse(savedWarehouseSettings)
        } else {
          warehouseSettings.value = { autoAddPallet: true }
        }
      } catch (e) {
        console.warn('无法读取仓库设置，使用默认值', e)
        warehouseSettings.value = { autoAddPallet: true }
      }
    }

    const changeLanguage = (lang) => {
      currentLanguage.value = lang
      localStorage.setItem('systemLanguage', lang)
      document.documentElement.setAttribute('lang', lang)
      ElMessage.success('语言设置已保存')
    }

    const changeTheme = (theme) => {
      currentTheme.value = theme
      localStorage.setItem('systemTheme', theme)
      applyTheme(theme)
      ElMessage.success('主题设置已保存')
    }

    const saveNotificationSettings = () => {
      localStorage.setItem(
        'notificationSettings',
        JSON.stringify(notificationSettings.value)
      )
      ElMessage.success('通知设置已保存')
    }

    const saveWarehouseSettings = () => {
      localStorage.setItem(
        'warehouseSettings',
        JSON.stringify(warehouseSettings.value)
      )
      ElMessage.success('仓库设置已保存')
    }

    const changeDefaultPage = (page) => {
      currentDefaultPage.value = page
      localStorage.setItem('defaultPage', page)
      ElMessage.success('默认页面设置已保存')
    }

    const goBack = () => router.back()

    onMounted(() => {
      loadSettings()
      applyTheme(currentTheme.value)
    })

    return {
      languages,
      themes,
      defaultPages,
      currentLanguage,
      currentTheme,
      notificationSettings,
      warehouseSettings,
      currentDefaultPage,
      changeLanguage,
      changeTheme,
      saveNotificationSettings,
      saveWarehouseSettings,
      changeDefaultPage,
      goBack
    }
  }
}
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
  background: var(--background-color);
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  color: var(--header-text);
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 12px;
  margin-top: calc(52px + env(safe-area-inset-top));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

.setting-card {
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.setting-card :deep(.el-card__header) {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
}

.setting-card :deep(.el-card__body) {
  padding: 12px 14px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-icon {
  font-size: 18px;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.option-radio {
  margin: 0 !important;
  width: 100%;
  height: auto;
  padding: 10px 12px;
  border-radius: 8px;
}

.option-radio :deep(.el-radio__label) {
  font-size: 14px;
  color: var(--text-primary);
}

.switch-list {
  display: flex;
  flex-direction: column;
}

.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
}

.switch-row:last-child {
  border-bottom: none;
}

.switch-label {
  font-size: 14px;
  color: var(--text-primary);
}
</style>
